/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import Heading from '@react/react-spectrum/Heading';
import Tooltip from '@react/react-spectrum/Tooltip';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import classnames from 'classnames';
import TranslationUtil from '../../../utils/translation';
import IconMapping from '../../../utils/iconMapping';
import { appTileAnalytics } from '../../../analytics';
import { SUBTEXT_TYPE, ANALYTICS } from '../../../utils/enums';
import { interpretKey } from '../../../utils/eventUtil';
import './index.scss';

export default @observer class AppTileHeader extends Component {
    @observable height = 0;

    componentDidMount() {
        if (this.updatedivElement && document.querySelector('#update-text-see-more')) {
            const diffHeight = document.querySelector('#update-text-see-more').offsetTop - this.updatedivElement.offsetHeight;
            this.setRelativeHeight(diffHeight);
        }
    }

    @action
    setRelativeHeight(updatedHeight) {
        this.height = updatedHeight;
    }

    onTileClick = () => {
        const {
            app,
            appSectionFilter,
            layoutId,
            appSectionBasedFields: {
                isCategorySection,
            },
        } = this.props;
        const {
            onAppAction,
            actionIds,
        } = app;
        onAppAction(actionIds.select, app, { isCategorySection });
        appTileAnalytics(ANALYTICS.EVENTS.APP_TILE_CLICK, {
            app,
            appSectionFilter,
            actionId: actionIds.select,
            viewType: ANALYTICS.VIEW_TYPE[layoutId],
        });
    }

    onSeeMoreClick = (e, actionId, app, appSectionBasedFields, appSectionFilter) => {
        const { onAppAction } = app;
        const {
            localizedUpdateDescription = [],
            seeMoreLinkUrl,
        } = appSectionBasedFields;
        e.stopPropagation();
        onAppAction(actionId, app, { localizedUpdateDescription, seeMoreLinkUrl });
        appTileAnalytics(ANALYTICS.EVENTS.SEE_MORE_CLICK, {
            actionId,
            app,
            appSectionFilter,
        });
    }

    handleIconLoadError(app, backUpIcon) {
        if (backUpIcon) {
            app.setIconUrl(backUpIcon);
        }
    }

    renderPlatformIcon(iconType, tooltipId, iconAriaText) {
        const { WebPage, DeviceDesktop, DevicePhone } = IconMapping;
        return (
            <OverlayTrigger key={iconType} placement="top" trigger={['hover', 'focus']} delayShow={1000}>
                <span>
                    {iconType === 'desktop'
                        && <DeviceDesktop key="desktop" aria-label={iconAriaText} className="appTile-Header-platform-icons" />
                    }
                    {iconType === 'mobile'
                        && <DevicePhone key="mobile" aria-label={iconAriaText} className="appTile-Header-platform-icons" />
                    }
                    {iconType === 'web'
                        && <WebPage key="mobile" aria-label={iconAriaText} className="appTile-Header-platform-icons" />
                    }
                </span>
                <Tooltip>
                    {TranslationUtil.getMessage(tooltipId)}
                </Tooltip>
            </OverlayTrigger>
        );
    }

    /**
    * Renders a list of supported platform icons
    */
    renderPlatformList(availablePlatforms, iconText) {
        if (!availablePlatforms) {
            return [];
        }
        const iconList = [];
        const { desktopLabel, mobileLabel, webLabel } = iconText || {};

        if (availablePlatforms.includes('desktop')) {
            iconList.push(this.renderPlatformIcon('desktop', 'ccx.appTile.platformIcons.desktop.tooltip', desktopLabel));
        }

        if (availablePlatforms.includes('mobile')) {
            iconList.push(this.renderPlatformIcon('mobile', 'ccx.appTile.platformIcons.mobile.tooltip', mobileLabel));
        }

        if (availablePlatforms.includes('web')) {
            iconList.push(this.renderPlatformIcon('web', 'ccx.appTile.platformIcons.web.tooltip', webLabel));
        }

        return (
            <div className="appTile-Header-platform-icons-wrapper">
                {iconList}
            </div>
        );
    }

    renderPlatform(platform) {
        return (
            <div className="tile-platform">
                {TranslationUtil.getMessage(`ccx.appTile.platformIcons.${platform}.tooltip`)}
            </div>
        );
    }

    renderSubText(subText, app) {
        if (!subText) {
            return [];
        }

        const {
            type, localizedText, action: moreInfoAction,
        } = subText;

        const { appSectionFilter } = this.props;
        switch (type) {
        case SUBTEXT_TYPE.INFO:
            return <div className="subText-information">{localizedText}</div>;
        case SUBTEXT_TYPE.ERROR:
            return (
                <div>
                    <span className="subText-error"> {localizedText} </span>
                    { moreInfoAction
                          && (
                              <span>
                                  <span> - </span>
                                  <span
                                      className="subText-moreInfoLink"
                                      onClick={
                                          (e) => {
                                              e.stopPropagation();
                                              app.onAppAction(moreInfoAction.actionId, app);
                                              appTileAnalytics(ANALYTICS.EVENTS.MORE_INFO_CLICK, {
                                                  app,
                                                  appSectionFilter,
                                                  actionId: moreInfoAction.actionId,
                                              });
                                          }
                                      }>
                                      {moreInfoAction.localizedText}
                                  </span>
                              </span>
                          )
                    }
                </div>
            );
        default: return [];
        }
    }

    renderTagline(localizedTagline) {
        if (!localizedTagline) {
            return [];
        }

        return (
            <Heading
                className="appTile-tagline"
                variant="subtitle3">
                {localizedTagline}
            </Heading>
        );
    }

    renderDescriptionList(app, appSectionBasedFields, appSectionFilter) {
        const {
            seeMoreLink = {},
            localizedUpdateDescription = [],
            seeMoreLinkUrl,
        } = appSectionBasedFields;
        const { actionId, localizedText } = seeMoreLink;

        const content = [];
        if (localizedUpdateDescription.length) {
            content.push((
                <ul key={0} className="appTile-update-text" ref={(updatedivElement) => { this.updatedivElement = updatedivElement; }}>
                    { localizedUpdateDescription.map(item => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
            ));
        }
        if (actionId && localizedText) {
            content.push((
                <span
                    key={1}
                    tabIndex={this.height <= 0 ? 0 : -1}
                    role="link"
                    id="update-text-see-more"
                    className={classnames('appTile-update-text-see-more', {
                        show: seeMoreLinkUrl,
                    })}
                    onKeyDown={interpretKey({
                        onEnter: [
                            (e) => {
                                this.onSeeMoreClick(
                                    e, actionId, app, appSectionBasedFields, appSectionFilter
                                );
                            }, true,
                        ],
                    })}
                    onClick={e => this.onSeeMoreClick(
                        e, actionId, app, appSectionBasedFields, appSectionFilter
                    )}>
                    ...<span>{localizedText}</span>
                </span>
            ));
        }

        return content;
    }

    renderHeaderText({
        app, subText, isHeaderClickable, sectionHeader,
        appSectionFilter, isCategorySection,
        subSectionFilter, updateTileVersion,
    }) {
        const {
            iconURL, tileVersion, appFamilyName, platform,
            isPDPDataAvailable, backUpIcon, sapcode, isSupportedForLongTerm,
        } = app;
        return (
            <div
                className={classnames('appTile-headerName-wrapper',
                    { clickable: (isPDPDataAvailable || app.isLaunchable) })}
                onClick={((isPDPDataAvailable || app.isLaunchable)
                    && !isHeaderClickable) ? this.onTileClick : undefined}>
                <img
                    className="appTile-headerName-wrapper-icon"
                    src={iconURL}
                    onError={() => this.handleIconLoadError(app, backUpIcon)}
                    alt={appFamilyName} />
                <div className="appTile-headerName-wrapper-text">
                    <div className="tile-version-wrapper">
                        <OverlayTrigger
                            disabled={!tileVersion}
                            placement="top"
                            trigger={['hover', 'focus']}
                            delayShow={500}>
                            <span className="appTile-primaryText">
                                {appFamilyName}
                            </span>
                            <Tooltip className="appTile-headerName-version-tooltip">
                                <span>
                                    {
                                        isSupportedForLongTerm
                                            ? TranslationUtil.getMessage('ccx.appTile.sapCode.tooltip.version.lts', { version: tileVersion, ltsStr: TranslationUtil.getMessage('ccx.otherVersions.info.longTermSupported') })
                                            : TranslationUtil.getMessage('ccx.appTile.sapCode.tooltip.version', { version: tileVersion })
                                    }
                                </span>
                            </Tooltip>
                        </OverlayTrigger>
                        {updateTileVersion
                            && (
                                <span className="appTile-codexVersion">
                                    {TranslationUtil.getMessage('ccx.appTile.version-prefix') + updateTileVersion}
                                </span>
                            )}
                        { !updateTileVersion && app.doesMultipleVersionsExistInSection(sapcode,
                            appSectionFilter, subSectionFilter, sectionHeader)
                            && (
                                <span className="appTile-codexVersion">
                                    {TranslationUtil.getMessage('ccx.appTile.version-prefix') + tileVersion}
                                </span>
                            )
                        }
                    </div>
                    {!isCategorySection
                        ? this.renderSubText(subText, app)
                        : this.renderPlatform(platform)}
                </div>
            </div>
        );
    }

    render() {
        const {
            app: { isPDPDataAvailable }, app, appSectionBasedFields, appSectionFilter,
            sectionHeader, subSectionFilter, appSection,
        } = this.props;
        const {
            subText, localizedTagline, availablePlatforms, isHeaderClickable,
            iconText, updateTileVersion, localizedUpdateDescription = [],
            isCategorySection,
        } = appSectionBasedFields;

        // get header based values
        // TODO: check subSection.sectionAppsData also
        let headerBasedAppData = {};
        if (appSection && appSection.sectionAppsData && appSection.sectionAppsData[app.sapcode]) {
            headerBasedAppData = appSection.sectionAppsData[app.sapcode];
        }
        const { tagLine } = headerBasedAppData;

        let content;
        if (localizedUpdateDescription.length) {
            content = this.renderDescriptionList(app, appSectionBasedFields, appSectionFilter);
        } else {
            content = this.renderTagline(tagLine || localizedTagline);
        }

        return (
            <div
                className={classnames('appTile-header-container', {
                    clickable: (isHeaderClickable
                    && (isPDPDataAvailable || app.isLaunchable)),
                })}
                onClick={isHeaderClickable && (isPDPDataAvailable || app.isLaunchable)
                    ? this.onTileClick : undefined}>
                <div className="appTile-header">
                    {this.renderHeaderText({
                        app,
                        subText,
                        isHeaderClickable,
                        sectionHeader,
                        appSectionFilter,
                        subSectionFilter,
                        updateTileVersion,
                        isCategorySection,
                    })}
                    {this.renderPlatformList(availablePlatforms, iconText)}
                </div>

                <div className="tagLine-update-container">
                    {content}
                </div>
            </div>
        );
    }
}
